import * as React from "react";
import {Loader} from "../../../../components/Utils/loader/Loader";
import {copyObj} from "../../alarms/subfilters/Commons";
import {
    GroupData,
    ITEM_EQUIPMENT_LAST_VISIT_DATE,
    PredefinedMethod,
    RequestInfo,
    SectionItem,
    SectionItemType,
    SingleData,
    ValueAdditionalData,
} from "../builder/models";
import {SectionItemBuilder} from "../builder/SectionItemBuilder";
import {ConfirmationDialog} from "../dialog/ConfirmationDialog";
import {ErrorDialog} from "../dialog/ErrorDialog";
import {EquipmentSettingsPopupController, EquipmentSettingsService} from "../EquipmentSettingsService";
import {ObjectFromEntries} from "../EquipmentSettingsUtils";
import {FieldEvent, FieldEventType} from "./Field";
import {AppTranslations} from "../../../../components/Utils/translations/AppTranslations";
import "./SectionFormBody.scss";
import {isHTMLTagSafe} from "../../../../Utils/Utils";
import {VisitRegistrationDialog} from "../dialog/VisitRegistrationDialog";

declare var AppTranslations: AppTranslations;


const POLLING_TIME = 10000;

interface SectionFormBodyProps {
    formData: any;
    reload?: () => void;
    reloadSections?: () => void;
    performAction: (action: string) => void;
    load?: () => void;
    formUpdater?: (f: (formData: any) => void) => void;
}

interface SectionFormBodyState {
    saved: any;
    fields: any;
    isPolling: boolean;
}

export class SectionFormBody extends React.Component<SectionFormBodyProps, SectionFormBodyState> {

    private intervalID: any;

    constructor(props) {
        super(props);
        this.state = {
            saved: copyObj(this.props.formData),
            fields: copyObj(this.props.formData),
            isPolling: true,
        };
    }

    public componentDidMount() {
        this.intervalID = setInterval(() => {
            if (this.state.isPolling) {
                this.props.load();
            }
        }, POLLING_TIME);

        this.props.formUpdater((formData) => {
            if (this.state.isPolling) {
                this.setState({
                    saved: copyObj(formData),
                    fields: copyObj(formData),
                });
            }
        });
    }

    public componentWillUnmount() {
        if (this.intervalID) {
            clearInterval(this.intervalID);
        }
    }

    public render() {
        return (
            <SectionItemBuilder
                itemsDescription={this.state.fields}
                onFieldChanged={this.onFieldChanged}
                onDataChanged={this.onDataChanged}
                fieldClassName={"form-fields"}
                onExecuteCommand={this.onExecuteCommand}
                reload={this.props.reload}
                reloadSections={this.props.reloadSections}
            />
        );
    }

    private onFieldChanged = (fieldID: string, e: FieldEvent) => {
        this.setState({
            fields: {
                ...this.state.fields,
                ...ObjectFromEntries(
                    Object.keys(this.state.fields).map((key) => {
                        return [key, {
                            ...this.state.saved[key],
                            editing: false,
                            editable: e.type !== FieldEventType.APPLY,
                        }];
                    }),
                ),
                [fieldID]: {
                    ...this.state.fields[fieldID],
                    editing: e.type === FieldEventType.EDIT,
                    editable: e.type !== FieldEventType.APPLY,
                    valid: false,
                },
            },
        }, () => {
            switch (e.type) {
                case FieldEventType.APPLY: {
                    this.applyParameterChange(this.state.fields[fieldID] as SectionItem)
                        .then(() => {
                            this.props.reload();
                        }).catch((err) => {
                        // DISPLAY an error message
                        if (typeof err === "boolean") {
                            if (err) {
                                this.showErrorMessage();
                            } else {
                                EquipmentSettingsPopupController.closePopup();
                            }
                            this.setState({
                                fields: copyObj(this.state.saved),
                                isPolling: true,
                            });
                        } else {
                            this.setDataErrorMessageOnEditMode(err?.fieldId, null, err?.errorMessage);
                        }
                    });
                    break;
                }
                case FieldEventType.CANCEL: {
                    this.setState({
                        fields: copyObj(this.state.saved),
                        isPolling: true,
                    });
                    break;
                }
                case FieldEventType.EDIT: {
                    this.setState({
                        isPolling: false,
                    });
                }
            }
        });
    }

    private onDataChanged = (fieldID: any, {groupID, value}) => {
        const oldSavedValue = !groupID ? this.state.saved[fieldID].data.value : undefined;
        this.setState({
            fields: {
                ...this.state.fields,
                [groupID ? groupID : fieldID]: {
                    ...this.state.fields[groupID ? groupID : fieldID],
                    data: !groupID
                        ? {
                            ...this.state.fields[fieldID].data,
                            value: {
                                ...this.state.fields[fieldID].data.value,
                                value,
                            },
                        }
                        : {
                            ...this.state.fields[groupID].data,
                            values: {
                                ...this.state.fields[groupID].data.values,
                                [fieldID]: {
                                    ...this.state.fields[groupID].data.values[fieldID],
                                    value: {
                                        ...this.state.fields[groupID].data.values[fieldID].value,
                                        value,
                                    },
                                },
                            },
                        },
                    valid: groupID
                        ? true
                        : value !== oldSavedValue.value && isHTMLTagSafe(value),
                    errorMessage: !isHTMLTagSafe(
                        value) ? AppTranslations.errorMessages.charactersNotAllowedInserted : ""
                },
            },
        });
    }

    private setDataErrorMessageOnEditMode = (fieldID: any, groupID: any, errorMessage: string) => {
        this.setState({
            fields: {
                ...this.state.saved,
                [groupID ? groupID : fieldID]: {
                    ...this.state.fields[groupID ? groupID : fieldID],
                    valid: false,
                    editing: true,
                    editable: true,
                    errorMessage: errorMessage
                },
            },
            isPolling: false,
        });
    }

    private applyParameterChange = (fieldData: SectionItem): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            let inPendingResponse: boolean = false;
            const closeDialog = () => {
                EquipmentSettingsPopupController.closePopup();
                reject(false);
            };

            const closeDialogWithError = () => {
                EquipmentSettingsPopupController.closePopup();
                reject(true);
            };
            const onDialogConfirm = (params: any) => {
                if (!inPendingResponse) {
                    inPendingResponse = true;
                    EquipmentSettingsPopupController.showPopup((
                        <div className={"loader-bg"}>
                            <Loader/>
                        </div>
                    ), () => null);
                    const requestInfo: RequestInfo = fieldData.action ? {
                        ...fieldData.action,
                        params: {
                            ...fieldData.action.params,
                            ...params,
                        },
                    } : null;

                    const sd = fieldData.data as SingleData;
                    EquipmentSettingsService.setParameterValue(
                        requestInfo,
                        (fieldData.type === SectionItemType.SINGLE)
                            ? {0: sd.value.value || ""}
                            : Object.keys((fieldData.data as GroupData).values).reduce((acc, key) => {
                                return {
                                    ...acc,
                                    [key]: ((fieldData.data as GroupData).values[key] as SingleData).value.value,
                                };
                            }, {}),
                    )
                        .then((response) => {
                            EquipmentSettingsPopupController.closePopup();
                            inPendingResponse = false;
                            if (response && response?.data?.success === false) {
                                reject({fieldId: fieldData.name, errorMessage: response?.data?.error});
                            } else {
                                resolve();
                            }
                        })
                        .catch((err) => {
                            console.error(err);
                            EquipmentSettingsPopupController.closePopup();
                            inPendingResponse = false;
                            closeDialogWithError();
                        });
                }
            };

            const singleData: SingleData = fieldData.type === SectionItemType.SINGLE
                ? fieldData.data as SingleData
                : undefined;

            const additionalData = singleData
                ? singleData.value.additionalData
                : undefined;

            if (
                singleData && additionalData
                && ((additionalData as ValueAdditionalData).confirmationDialog
                    || (additionalData[singleData.value.value]
                        && (additionalData[singleData.value.value] as ValueAdditionalData).confirmationDialog)
                )) {
                const confirmationDialog = (additionalData as ValueAdditionalData).confirmationDialog
                    ? (additionalData as ValueAdditionalData).confirmationDialog
                    : (additionalData[singleData.value.value] as ValueAdditionalData).confirmationDialog;

                EquipmentSettingsPopupController.showPopup((
                    <ConfirmationDialog
                        confirmationDialogInfo={confirmationDialog}
                        onConfirm={onDialogConfirm}
                        onCancel={closeDialog}
                    />
                ), closeDialog);
            } else if (fieldData.confirmationDialog) {
                EquipmentSettingsPopupController.showPopup((
                    <ConfirmationDialog
                        confirmationDialogInfo={fieldData.confirmationDialog}
                        onConfirm={onDialogConfirm}
                        onCancel={closeDialog}
                    />
                ), closeDialog);
            } else {
                onDialogConfirm({});
            }
        });
    }

    private onExecuteCommand = (request: RequestInfo, actionName: string, actionGuid?: string,
                                equipmentGuid?: string) => {
        if (request) {
            let requestIsPending = false;
            const sI = this.state.fields[actionName] as SectionItem;

            const confirmationDialog = sI.confirmationDialog;

            const closeDialog = () => {
                EquipmentSettingsPopupController.closePopup();
            };

            const manageErrorMessage = () => {
                requestIsPending = false;
                EquipmentSettingsPopupController.closePopup();
                this.showErrorMessage();
            }

            const showLoader = () => {
                EquipmentSettingsPopupController.showPopup((
                    <div className={"loader-bg"}>
                        <Loader/>
                    </div>
                ), () => null);
            }

            const executeCommand = () => {
                showLoader()
                if (!requestIsPending) {
                    requestIsPending = true;
                    EquipmentSettingsService.executeCommand(request)
                        .then(() => {
                            // Reload page
                            requestIsPending = false;
                            EquipmentSettingsPopupController.closePopup();
                            if (request.method === PredefinedMethod.DELETE) {
                                this.props.performAction("actionGoBack");
                            }
                        })
                        .catch(() => {
                            manageErrorMessage()
                        });
                }
            };
            if (actionGuid === ITEM_EQUIPMENT_LAST_VISIT_DATE) {
                EquipmentSettingsPopupController.showPopup((
                    <VisitRegistrationDialog
                        onClose={closeDialog}
                        manageErrorMessage={manageErrorMessage}
                        equipmentGuid={equipmentGuid}
                        reload={this.props.reloadSections}
                    />
                ), closeDialog);
            }
            if (confirmationDialog) {
                EquipmentSettingsPopupController.showPopup((
                    <ConfirmationDialog
                        confirmationDialogInfo={confirmationDialog}
                        onConfirm={executeCommand}
                        onCancel={closeDialog}
                    />
                ), closeDialog);
            }
        }
    }

    private showErrorMessage = () => {
        const closePopup = () => {
            EquipmentSettingsPopupController.closePopup();
        };
        EquipmentSettingsPopupController.showPopup((
            <ErrorDialog onOk={closePopup}/>
        ));
    }
}
